export const DOCUMENT_TYPE_OPTIONS = [
  { label: "Carte d'identit\u00e9", value: 'CNI' },
  { label: 'Passeport', value: 'PASSPORT' },
]

export const GENDER_OPTIONS = [
  { label: 'Mr', value: 'MALE' },
  { label: 'Mme', value: 'FEMALE' },
] as const

export const MONTH_OPTIONS = [
  { label: 'Janvier', value: '01' },
  { label: 'Février', value: '02' },
  { label: 'Mars', value: '03' },
  { label: 'Avril', value: '04' },
  { label: 'Mai', value: '05' },
  { label: 'Juin', value: '06' },
  { label: 'Juillet', value: '07' },
  { label: 'Août', value: '08' },
  { label: 'Septembre', value: '09' },
  { label: 'Octobre', value: '10' },
  { label: 'Novembre', value: '11' },
  { label: 'Décembre', value: '12' },
] as const

export interface PassengersCount {
  adults: number
  babies: number
  infants: number
  seniors: number
  youngsters: number
}

type PassengerCategoryGroup = 'adults' | 'babies' | 'infants' | 'seniors' | 'youngsters'

export function generateDayOptions(month: string, year: string) {
  const maxDays = getDaysInMonth(month, year)
  return Array.from({ length: maxDays }, (_, i) => {
    const day = String(i + 1).padStart(2, '0')
    return { label: day, value: day }
  })
}

export function generateYearOptions() {
  const currentYear = new Date().getFullYear()
  const startYear = currentYear - 120
  return Array.from({ length: currentYear - startYear + 1 }, (_, i) => {
    const year = String(currentYear - i)
    return { label: year, value: year }
  })
}

function getDaysInMonth(month: string, year: string): number {
  if (!month || !year) return 31
  const m = Number.parseInt(month, 10)
  const y = Number.parseInt(year, 10)
  if (Number.isNaN(m) || Number.isNaN(y)) return 31
  return new Date(y, m, 0).getDate()
}

const CATEGORY_AGE_DESCRIPTIONS = {
  adults: 'De 25 à 59 ans',
  babies: 'Moins de 2 ans',
  infants: 'De 2 à 11 ans',
  seniors: 'Plus de 60 ans',
  youngsters: 'De 12 à 24 ans',
} satisfies Record<PassengerCategoryGroup, string>

export function getPassengerCategory(
  passengers: PassengersCount,
  index: number
): PassengerCategoryGroup {
  const groups: { category: PassengerCategoryGroup; count: number }[] = [
    { category: 'adults', count: passengers.adults },
    { category: 'seniors', count: passengers.seniors },
    { category: 'youngsters', count: passengers.youngsters },
    { category: 'infants', count: passengers.infants },
    { category: 'babies', count: passengers.babies },
  ]
  let offset = 0
  for (const group of groups) {
    if (index < offset + group.count) {
      return group.category
    }
    offset += group.count
  }
  return 'adults'
}

export function getPassengerLabel(
  passengers: {
    adults: number
    babies: number
    infants: number
    seniors: number
    youngsters: number
  },
  index: number
): string {
  const groups: { category: PassengerCategoryGroup; count: number; label: string }[] = [
    { category: 'adults', count: passengers.adults, label: 'Adulte' },
    { category: 'seniors', count: passengers.seniors, label: 'Senior' },
    { category: 'youngsters', count: passengers.youngsters, label: 'Jeune' },
    { category: 'infants', count: passengers.infants, label: 'Enfant' },
    { category: 'babies', count: passengers.babies, label: 'Bébé' },
  ]
  let offset = 0
  for (const group of groups) {
    if (index < offset + group.count) {
      return `${group.label} - ${index - offset + 1} (${CATEGORY_AGE_DESCRIPTIONS[group.category]})`
    }
    offset += group.count
  }
  return `Voyageur ${index + 1}`
}

export function getPassengerType(
  passengers: {
    adults: number
    babies: number
    infants: number
    seniors: number
    youngsters: number
  },
  index: number
): 'Adult' | 'Child' | 'Infant' {
  const groups: { count: number; type: 'Adult' | 'Child' | 'Infant' }[] = [
    { count: passengers.adults, type: 'Adult' },
    { count: passengers.seniors, type: 'Adult' },
    { count: passengers.youngsters, type: 'Adult' },
    { count: passengers.infants, type: 'Child' },
    { count: passengers.babies, type: 'Infant' },
  ]
  let offset = 0
  for (const group of groups) {
    if (index < offset + group.count) {
      return group.type
    }
    offset += group.count
  }
  return 'Adult'
}

export function getTotalPassengerCount(passengers: {
  adults: number
  babies: number
  infants: number
  seniors: number
  youngsters: number
}): number {
  return (
    passengers.adults +
    passengers.babies +
    passengers.infants +
    passengers.seniors +
    passengers.youngsters
  )
}
